/**
 * Symphenia – Einheitliche Formular-Styles
 *
 * Alle Formulare, Dialoge und Einstellungsseiten verwenden diese Konstanten,
 * damit Look & Feel konsistent bleiben und Anpassungen nur an einer Stelle
 * gemacht werden müssen.
 *
 * Farbgebung: Rahmenfarbe und Überschriften-Farbe stammen immer aus der
 * primären Akzentfarbe des gewählten Designs (--primary via Tailwind `text-primary`
 * / `border-primary`). Hintergrund der Karten hebt sich durch `bg-muted/20`
 * vom Seitenhintergrund ab.
 */

// ── Seitenrahmen ──────────────────────────────────────────────────────────────
//
// Eine Seite entscheidet genau eine Sache selbst: ob ihr Inhalt in die Breite
// darf. Beide Konstanten enthalten denselben Innenabstand — der Unterschied ist
// allein die Deckelung.
//
// Der Anlass (9.9.2026): Der Monatsbericht stand auf `max-w-5xl`, während seine
// Tabelle mit dreizehn Spalten darin waagrecht scrollte — links und rechts
// blieb Rand ungenutzt. Die Listenseiten machten es längst richtig, aber jede
// mit derselben abgetippten Klassenkette; die eine Seite, die abwich, fiel
// nicht auf.

/**
 * Volle Seitenbreite — für **Listen, Tabellen und Auswertungen**.
 *
 * Bewusst ohne Obergrenze, auch bei wenigen Spalten: Eine Tabelle, die je nach
 * Spaltenzahl mal zentriert und mal randlos steht, wirkt beim Umschalten
 * zwischen zwei Auswertungen wie ein Fehler. Reicht der Platz nicht, scrollt
 * der Tabellen-Container waagrecht (`overflow-x-auto`) — nicht die Seite.
 */
export const pageWide = 'space-y-6 p-4 sm:p-6'

/**
 * Begrenzte Lesebreite — für **Formulare und Einstellungsseiten**.
 *
 * Dort ist die Breite kein Gewinn: Ein Eingabefeld über 1400 Pixel liest sich
 * schlechter als eines über 700.
 */
export const pageNarrow = 'mx-auto max-w-3xl space-y-6 p-4 sm:p-6'

// ── Karten / Sektionsboxen ────────────────────────────────────────────────────

/** Äußere Formular-Karte (große Sektion, z. B. Clock-Mode, Manuelle Erfassung) */
export const formCard = 'rounded-lg border border-primary/30 bg-muted/20 p-6'

/** Kompakte Karte (z. B. Tageszusammenfassung, kleinere Sektionen) */
export const formCardSm = 'rounded-lg border border-primary/30 bg-muted/20 p-4'

/** Einzel-Eintrag / Listenzeile (hebt sich von der Karte ab) */
export const entryCard = 'rounded-lg border border-primary/30 bg-background p-4 transition-colors'

/** Eintrag im Lösch-Zustand */
export const entryCardDeleting = 'border-red-400 bg-red-50 dark:border-red-600 dark:bg-red-950/40'

/** Gruppen-Block innerhalb eines Formulars (z. B. Zusatzfeld-Gruppe) */
export const fieldGroup = 'space-y-3 rounded-md border border-border/60 bg-muted/20 p-3'

// ── Überschriften ─────────────────────────────────────────────────────────────

/** Seiten-Überschrift (h1) */
export const pageTitle = 'text-2xl font-bold text-primary'

/** Abschnitts-Überschrift (h2, z. B. „Heutige Einträge") */
export const sectionTitle = 'text-xl font-semibold text-primary'

/** Karten-Überschrift (h3, z. B. „Tageszusammenfassung") */
export const cardTitle = 'text-base font-semibold text-primary'

/** Gruppen-Label innerhalb einer Karte */
export const groupLabel = 'text-xs font-semibold uppercase tracking-wide text-primary'

// ── Formularfelder ────────────────────────────────────────────────────────────

/** Label über einem Eingabefeld */
export const fieldLabel = 'block text-xs font-semibold uppercase tracking-wide text-muted-foreground'

/** Standard-Texteingabe */
export const inputBase = 'w-full rounded-md border border-input bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring'

/** Dropdown / Select */
export const selectBase = 'w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring'

/** Textarea */
export const textareaBase = 'w-full rounded-md border border-input bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring'

/** Feld-Wrapper (Label + Input) – einspaltiger Abstand */
export const fieldWrapper = 'space-y-1.5'

/** Feld-Wrapper – zweispaltiges Grid (z. B. Von / Bis) */
export const fieldGrid2 = 'grid grid-cols-2 gap-3'

/** Feld-Wrapper – dreispaltiges Grid */
export const fieldGrid3 = 'grid grid-cols-3 gap-3'

// ── Overlays: Dialoge, Dropdowns, Toasts ──────────────────────────────────────
// REGEL: Kein backdrop-blur, kein bg-opacity, kein /XX Transparenz-Suffix.
// Alle schwebenden UI-Elemente verwenden immer einen volldeckenden Hintergrund.
//
// ⚠️  WICHTIG: Tailwind-Klassen allein reichen für Dropdowns/Dialoge NICHT aus!
//     bg-popover kann transparent erscheinen wenn:
//       a) 'popover' fehlt in tailwind.config.ts (→ wurde ergänzt, aber Rebuild nötig)
//       b) <button>-Elemente Browser-Textfarbe überschreiben (ignorieren Vererbung)
//
//     LÖSUNG: Hintergrund + Textfarbe IMMER als inline style setzen:
//       style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
//     Das liest die ThemeProvider-Variablen direkt, ohne Tailwind-Umweg.
//     Siehe FORM-STYLES.md → Abschnitt "Overlays" für vollständiges Beispiel.

/** Dropdown-Menü – Tailwind-Klassen für Layout/Border/Shadow.
 *  Hintergrund MUSS zusätzlich per inline style gesetzt werden:
 *  style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }} */
export const dropdownMenu = 'absolute z-50 min-w-[160px] rounded-md border border-border bg-popover shadow-lg text-sm'

/** Einzelnes Dropdown-Element – Textfarbe MUSS per inline style gesetzt werden:
 *  style={{ color: 'hsl(var(--card-foreground))' }} */
export const dropdownItem = 'w-full px-3 py-2 text-left text-popover-foreground hover:bg-accent hover:text-accent-foreground transition-colors first:rounded-t-md last:rounded-b-md cursor-pointer'

/** Toast / Meldungsfenster */
export const toastBase = 'fixed bottom-4 right-4 z-50 rounded-md border border-border bg-popover px-4 py-3 shadow-lg text-sm text-popover-foreground'

// ── Interne Formulare (Inline-Edit etc.) ────────────────────────────────────

/** Inline-Edit-Bereich unterhalb eines Eintrags */
export const inlineEditPanel = 'mt-3 space-y-3 rounded-md border border-border bg-muted/20 p-3'

/** Label im Inline-Edit-Bereich */
export const inlineLabel = 'block text-xs text-muted-foreground'

/** Input im Inline-Edit-Bereich */
export const inlineInput = 'w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring'
